// Pure mapping from the journey API onto what the Update Journey screen renders.
// No React and no I/O. The server decides what is possible (`allowed_actions`,
// `capabilities`, each city's `visit_state`); this only turns that into UI props.
import { formatDate, formatDateRange } from "../../utils/tspFormat";
import { VISIT_STATE_TO_CITY_STATUS, WORKFLOW_ACTION_UI } from "../../constants/tspJourneyStatus";

// The server's workflow actions: a journey starts when created and completes by
// itself when every stop is visited, so cancelling is the only one.
const ACTION_ORDER = ["cancel"];

function gpsText(visit) {
  if (visit.gps_latitude === null || visit.gps_longitude === null) return "—";
  return `${visit.gps_latitude}, ${visit.gps_longitude}`;
}

// { location_id -> { books, average_score } } from GET /api/tsp/stats, for the
// "142 books · ★ 4.8 rating" chips the design shows on each visited city row.
// Absent stats permission or data leaves a city with no chip, never a fake one.
export function buildCityStatsByLocation(cities = []) {
  const byLocation = new Map();
  cities.forEach((city) => byLocation.set(city.location_id, { books: city.books, averageScore: city.average_score }));
  return byLocation;
}

// A journey place's own page: its journey, opened on that place (the timeline
// opens and scrolls to it — see buildTimelineCities' `openPlaceId`).
export const journeyPlacePath = (journeyId, placeId) => `/tsp/journeys/${journeyId}${placeId ? `?place=${placeId}` : ""}`;

// Route cities (API) + city visits (API, newest first) -> the timeline's cities.
// A city's status comes straight from the server's derived `visit_state`:
// visited -> "completed", current -> "live", not_visited -> "not_visited".
export function buildTimelineCities(route = [], visits = [], statsByLocation = new Map(), openPlaceId = null, routeOverrides = {}) {
  const latestVisitByLocation = new Map();
  (visits || []).forEach((visit) => {
    const locId = visit.location_id || visit.location?.id;
    if (locId && !latestVisitByLocation.has(locId)) latestVisitByLocation.set(locId, visit);
  });

  const cities = (route || []).map((city) => {
    const visit = latestVisitByLocation.get(city.location_id);
    const stats = statsByLocation.get(city.location_id);
    const labelParts = (city.label || "").split(",");
    const cityName = city.city_name || (labelParts[0] ? labelParts[0].trim() : "");
    const stateName = city.state_name || (labelParts[1] ? labelParts[1].trim() : "");
    const stateId = city.state_id || null;
    const overriddenPlannedDate = routeOverrides[String(city.id)] !== undefined ? routeOverrides[String(city.id)] : city.planned_date;
    const plannedDate = overriddenPlannedDate || city.first_visit_date || "";
    return {
      id: String(city.id),
      order: city.sequence_number,
      name: city.label,
      cityName,
      stateName,
      stateId,
      locationId: city.location_id,
      // The stop's own map point, shown in Update Location (null when none is saved).
      hasLocation: Number.isFinite(city.latitude) && Number.isFinite(city.longitude),
      latitude: Number.isFinite(city.latitude) ? city.latitude : null,
      longitude: Number.isFinite(city.longitude) ? city.longitude : null,
      plannedDate,
      planned_date: plannedDate,
      // The stop's recorded visit, named by the server's route view (null while not visited).
      cityVisitId: city.latest_visit_id ?? (visit ? visit.id : null),
      dateRange: formatDateRange(city.first_visit_date || plannedDate, city.last_visit_date || plannedDate),
      status: VISIT_STATE_TO_CITY_STATUS[city.visit_state],
      books: stats && stats.books > 0 ? stats.books : null,
      averageScore: stats && stats.averageScore ? stats.averageScore : null,
      visitInfo: visit
        ? {
            date: formatDate(visit.visit_date),
            time: visit.visit_time || "—",
            gps: gpsText(visit),
            remarks: visit.remarks || "—",
            areasVisited: visit.areas_visited,
          }
        : null,
      defaultOpen: false,
      focused: false,
    };
  });

  // A place asked for by id (?place= — the Journey List, a map marker) opens and
  // is brought into view. Otherwise: the city the team is in now, else the first
  // one with visit details, else the first city.
  const asked = openPlaceId ? cities.find((city) => city.id === String(openPlaceId)) : null;
  if (asked) asked.focused = true;
  const first = asked || cities.find((city) => city.status === "live" && city.visitInfo) || cities.find((city) => city.visitInfo) || cities[0];
  if (first) first.defaultOpen = true;
  return cities;
}

// The rail's Quick Actions for THIS journey and caller. Nothing is listed that the
// server would refuse: workflow buttons come from `allowed_actions`.
export function buildQuickActions(journey) {
  const actions = [];
  ACTION_ORDER.filter((name) => journey.allowed_actions.includes(name)).forEach((name) => {
    actions.push({ key: name, label: WORKFLOW_ACTION_UI[name].label, tone: WORKFLOW_ACTION_UI[name].tone });
  });
  return actions;
}

export function describeDuration(journey) {
  if (!journey.start_date || !journey.end_date) return null;
  const days = Math.round((Date.parse(`${journey.end_date}T00:00:00Z`) - Date.parse(`${journey.start_date}T00:00:00Z`)) / 86400000) + 1;
  return days > 0 ? `${days} ${days === 1 ? "day" : "days"}` : null;
}
